import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../utils/api'
import Navbar from '../components/Navbar'

const TIERS = {
  quick: {
    name: 'Exposure Report',
    price: 295,
    description: 'Full 200+ broker scan, breach details, 7 PIRs answered, opt-out links',
  },
  full: {
    name: 'Executive Intelligence Report',
    price: 2500,
    description: 'Human analyst review, 30-min threat briefing call, full 1000+ source scan, all 24 PIRs',
  },
  annual: {
    name: 'Annual Monitoring',
    price: 1500,
    description: 'Continuous breach monitoring, quarterly updates, priority analyst support (requires Executive Report)',
  },
}

export default function Checkout() {
  const { tier } = useParams<{ tier: keyof typeof TIERS }>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  const selectedTier = tier && TIERS[tier]

  useEffect(() => {
    if (!selectedTier) {
      navigate('/')
    }
  }, [selectedTier, navigate])

  async function handleCheckout() {
    if (!tier) return
    setLoading(true)
    setError('')

    try {
      const response = await api.post('/order', { tier })
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create checkout session')
      setLoading(false)
    }
  }

  if (!selectedTier) {
    return null
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      <div className="pt-24 pb-16 px-4 flex items-center justify-center min-h-screen">
        <div className="card max-w-md w-full">
          <h1 className="text-2xl font-bold mb-2">Complete Your Order</h1>
          <p className="text-dark-400 mb-6">
            You're about to purchase a {selectedTier.name}.
          </p>

          <div className="bg-dark-800 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <div className="font-bold">{selectedTier.name}</div>
                <div className="text-dark-400 text-sm">{selectedTier.description}</div>
              </div>
              <div className="text-2xl font-bold text-ginger-500">
                ${selectedTier.price.toLocaleString()}
              </div>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-2 text-sm text-dark-300">
              <span className="text-green-400">✓</span>
              Report delivered within 48 hours
            </div>
            <div className="flex items-center gap-2 text-sm text-dark-300">
              <span className="text-green-400">✓</span>
              Secure payment via Stripe
            </div>
            <div className="flex items-center gap-2 text-sm text-dark-300">
              <span className="text-green-400">✓</span>
              Encrypted data handling
            </div>
            <div className="flex items-center gap-2 text-sm text-dark-300">
              <span className="text-green-400">✓</span>
              Partner referrals included
            </div>
          </div>

          {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

          <button
            onClick={handleCheckout}
            disabled={loading}
            className="btn btn-primary w-full"
          >
            {loading ? 'Redirecting to payment...' : `Pay $${selectedTier.price.toLocaleString()}`}
          </button>

          <p className="text-dark-500 text-xs text-center mt-4">
            You'll be redirected to Stripe for secure payment processing.
          </p>
        </div>
      </div>
    </div>
  )
}
